import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  ChevronLeft, 
  FolderKanban, 
  CheckCircle2, 
  Clock, 
  FileText, 
  MessageSquare, 
  Download, 
  User,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { PUBLISHING_STAGES_CONFIG } from '@ftt/shared';
import { Breadcrumb } from '../../components/common/Breadcrumb';

export const ClientProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data: project, isLoading, error } = useQuery({
    queryKey: ['client-project-detail', id],
    queryFn: () => api.get(`/client/projects/${id}`).then((res) => res.data.data),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="p-12 text-center text-xs font-mono text-slate-500">Loading project details...</div>;
  }

  if (error || !project) {
    return (
      <div className="p-12 text-center space-y-4">
        <h2 className="text-xl font-serif font-bold text-slate-900">Project Not Found</h2>
        <Link to="/client/projects" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to projects
        </Link>
      </div>
    );
  }

  const milestones = project.milestones || [];
  const documents = project.documents || [];
  const currentStageConfig = PUBLISHING_STAGES_CONFIG.find((s) => s.status === project.status);
  const completedMilestones = milestones.filter((m: any) => m.status === 'COMPLETED').length;
  const progressPct = Math.round((completedMilestones / (milestones.length || 12)) * 100);

  return (
    <div className="space-y-6">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'Dashboard', href: '/client/dashboard' },
            { label: 'Projects', href: '/client/projects' },
            { label: project.projectCode },
          ]}
        />
      </div>

      {/* Project Header Card */}
      <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-3 py-1 rounded bg-slate-100 text-slate-700 font-bold uppercase">
                {project.projectCode}
              </span>
              <span className="text-xs text-brand-700 font-mono font-bold">
                {project.category}
              </span>
            </div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">
              {project.title}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500">
              Author: <strong>{project.author}</strong> &bull; Format: {project.projectType}
            </p>
          </div>

          <div className="text-right flex sm:flex-col items-center sm:items-end justify-between gap-2">
            <span className="text-[10px] text-slate-400 font-mono uppercase">Current Project Stage</span>
            <span className="text-sm font-bold text-brand-800 bg-brand-50 px-3.5 py-1.5 rounded-lg border border-brand-200 inline-block">
              {currentStageConfig?.label || project.status}
            </span>
          </div>
        </div>

        {/* Project Meta Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <span className="text-slate-400 block text-[10px] uppercase">Assigned Staff Lead</span>
            <span className="font-bold text-slate-900">
              {project.assignedStaff ? `${project.assignedStaff.firstName} ${project.assignedStaff.lastName}` : 'Senior Editorial Committee'}
            </span>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <span className="text-slate-400 block text-[10px] uppercase">Overall Progress</span>
            <span className="font-bold text-brand-800">
              {completedMilestones} of {milestones.length} Milestones ({progressPct}%)
            </span>
          </div>

          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <span className="text-slate-400 block text-[10px] uppercase">Target Publication</span>
            <span className="font-bold text-slate-900">
              {project.targetPubDate ? new Date(project.targetPubDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Scheduled per agreement'}
            </span>
          </div>
        </div>

        {/* Overview Synopsis */}
        {project.description && (
          <div className="space-y-1 text-xs text-slate-600 font-sans border-t border-slate-100 pt-4">
            <span className="font-bold text-slate-900 uppercase text-[11px] block">Project Description / Scope</span>
            <p>{project.description}</p>
          </div>
        )}
      </div>

      {/* 12-Stage Milestones Timeline */}
      <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h2 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-brand-700" />
              <span>12-Stage Editorial &amp; Production Roadmap</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Live progression of your manuscript through each publishing discipline.</p>
          </div>
        </div>

        <div className="space-y-4">
          {milestones.map((m: any) => {
            const isCompleted = m.status === 'COMPLETED';
            const isInProgress = m.status === 'IN_PROGRESS';

            return (
              <div
                key={m.id}
                className={`p-4 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                  isCompleted
                    ? 'bg-emerald-50/40 border-emerald-200'
                    : isInProgress
                    ? 'bg-brand-50/50 border-brand-300 ring-1 ring-brand-300'
                    : 'bg-slate-50/50 border-slate-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : isInProgress ? (
                      <div className="w-5 h-5 rounded-full border-2 border-brand-600 border-t-transparent animate-spin"></div>
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-slate-300 bg-white flex items-center justify-center font-mono text-[10px] text-slate-400 font-bold">
                        {m.orderIndex}
                      </div>
                    )}
                  </div>

                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-serif font-bold text-sm text-slate-900">{m.title}</span>
                      <span className={`text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded ${
                        isCompleted ? 'bg-emerald-100 text-emerald-800' : isInProgress ? 'bg-brand-100 text-brand-800' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {m.status}
                      </span>
                    </div>
                    {m.description && <p className="text-xs text-slate-500 font-sans">{m.description}</p>}
                  </div>
                </div>

                <div className="text-xs font-mono text-slate-400 text-right shrink-0">
                  {m.completedAt ? (
                    <span className="text-emerald-700 font-medium">Completed on {new Date(m.completedAt).toLocaleDateString()}</span>
                  ) : m.dueDate ? (
                    <span>Due: {new Date(m.dueDate).toLocaleDateString()}</span>
                  ) : (
                    <span>Pending scheduled queue</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Project Documents */}
      <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand-700" />
            <span>Files Associated with this Project</span>
          </h2>
          <Link to="/client/documents" className="text-xs font-bold text-brand-700 hover:underline">
            Go to Document Centre &rarr;
          </Link>
        </div>

        {documents.length === 0 ? (
          <div className="p-6 text-center text-xs text-slate-500">No documents attached to this project yet.</div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3.5 rounded-lg bg-slate-50 border border-slate-100 text-xs"
              >
                <div className="space-y-0.5">
                  <span className="font-bold text-slate-900 block">{doc.title}</span>
                  <span className="text-[10px] text-slate-400 font-mono">
                    {doc.category} &bull; {Math.round(doc.fileSize / 1024)} KB &bull; Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <a
                  href={`/api/client/documents/${doc.id}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded bg-white border border-slate-200 text-slate-700 font-bold hover:border-brand-300 flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Download</span>
                </a>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
